// API Types and Interfaces for 1vX.gg
// HTTP responses, error handling, and service interfaces

import { QueryVariables } from './content';

// Base API response structure
export interface APIResponse<T = unknown> {
  data?: T;
  errors?: APIError[];
  extensions?: Record<string, unknown>;
}

export interface APIError {
  message: string;
  code?: string;
  path?: (string | number)[];
  extensions?: {
    code?: string;
    exception?: {
      stacktrace?: string[];
    };
  };
}

// HTTP status types
export type HTTPStatus =
  | 200 // OK
  | 201 // Created
  | 204 // No Content
  | 400 // Bad Request
  | 401 // Unauthorized
  | 403 // Forbidden
  | 404 // Not Found
  | 500 // Internal Server Error
  | 502 // Bad Gateway
  | 503; // Service Unavailable

export interface HTTPError {
  status: HTTPStatus;
  statusText: string;
  message: string;
  timestamp: string;
  path?: string;
}

// GraphQL query state
export interface QueryState<T = unknown> {
  data?: T;
  loading: boolean;
  error?: Error | APIError;
  networkStatus?: NetworkStatus;
}

export enum NetworkStatus {
  loading = 1,
  setVariables = 2,
  fetchMore = 3,
  refetch = 4,
  poll = 6,
  ready = 7,
  error = 8,
}

// Service interfaces
export interface DataService<T, V extends QueryVariables = QueryVariables> {
  query(variables?: V): Promise<T>;
  refetch(variables?: V): Promise<T>;
  loading: boolean;
  error?: Error;
  data?: T;
}

export interface CacheService {
  get<T>(key: string): T | undefined;
  set<T>(key: string, value: T, ttl?: number): void;
  delete(key: string): void;
  clear(): void;
  has(key: string): boolean;
}

export interface MetricsService {
  trackPageView(path: string, metadata?: Record<string, unknown>): void;
  trackEvent(event: string, properties?: Record<string, unknown>): void;
  trackError(error: Error, context?: Record<string, unknown>): void;
  trackTiming(
    name: string,
    duration: number,
    metadata?: Record<string, unknown>,
  ): void;
}

// Pagination types
export interface PaginationParams {
  page?: number;
  limit?: number;
  offset?: number;
  cursor?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextCursor?: string;
  previousCursor?: string;
}

// Authentication types
export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  permissions: Permission[];
  avatar?: string;
  isVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

export type UserRole = 'admin' | 'editor' | 'author' | 'subscriber';

export interface Permission {
  resource: string;
  action: string;
  conditions?: Record<string, unknown>;
}

export interface AuthState {
  user?: AuthUser;
  isAuthenticated: boolean;
  isLoading: boolean;
  error?: AuthError;
}

export interface AuthError {
  code: string;
  message: string;
  field?: string;
}

// File upload types
export interface FileUpload {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  url?: string;
}

export interface UploadResponse {
  id: string;
  url: string;
  filename: string;
  size: number;
  mimeType: string;
  metadata?: Record<string, unknown>;
}

// Analytics types
export interface PageViewEvent {
  path: string;
  title?: string;
  referrer?: string;
  userAgent?: string;
  timestamp: string;
  sessionId: string;
  userId?: string;
}

export interface CustomEvent {
  name: string;
  properties: Record<string, unknown>;
  timestamp: string;
  sessionId: string;
  userId?: string;
}

export interface PerformanceMetric {
  name: string;
  duration: number;
  startTime: number;
  endTime: number;
  metadata?: Record<string, unknown>;
}

// Feature flags
export interface FeatureFlag {
  key: string;
  enabled: boolean;
  variants?: Record<string, unknown>;
  conditions?: FeatureFlagCondition[];
}

export interface FeatureFlagCondition {
  type: 'user_role' | 'user_id' | 'percentage' | 'date_range';
  value: unknown;
}

// Configuration types
export interface AppConfig {
  api: {
    graphqlUrl: string;
    restUrl?: string;
    timeout: number;
    retryAttempts: number;
  };
  auth: {
    enabled: boolean;
    providers: string[];
  };
  features: Record<string, boolean>;
  analytics: {
    enabled: boolean;
    trackingId?: string;
  };
  cdn: {
    baseUrl: string;
    imageOptimization: boolean;
  };
}

// Environment types
export type Environment = 'development' | 'staging' | 'production';

export interface EnvironmentConfig {
  environment: Environment;
  debug: boolean;
  apiUrl: string;
  cdnUrl: string;
}

// Webhook types
export interface WebhookPayload {
  event: string;
  data: Record<string, unknown>;
  timestamp: string;
  signature?: string;
}

export interface WebhookHandler {
  event: string;
  handler: (payload: WebhookPayload) => Promise<void>;
}

// Rate limiting
export interface RateLimitConfig {
  windowMs: number;
  maxRequests: number;
  skipFailedRequests?: boolean;
  skipSuccessfulRequests?: boolean;
}

export interface RateLimitState {
  remaining: number;
  resetTime: number;
  limit: number;
}

// Health check types
export interface HealthCheck {
  status: 'healthy' | 'degraded' | 'unhealthy';
  checks: Record<string, ServiceHealth>;
  timestamp: string;
  uptime: number;
}

export interface ServiceHealth {
  status: 'up' | 'down' | 'degraded';
  responseTime?: number;
  error?: string;
  lastCheck: string;
}

// Validation types
export interface ValidationRule {
  field: string;
  rules: string[];
  message?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string[]>;
  warnings: Record<string, string[]>;
}

// Background job types
export interface Job {
  id: string;
  type: string;
  data: Record<string, unknown>;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress?: number;
  result?: unknown;
  error?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface JobQueue {
  name: string;
  priority: number;
  retryAttempts: number;
  retryDelay: number;
}

// Notification types
export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  duration?: number;
  actions?: NotificationAction[];
  metadata?: Record<string, unknown>;
}

export interface NotificationAction {
  label: string;
  handler: () => void;
  style?: 'primary' | 'secondary' | 'danger';
}
